import { useEffect, useState, useMemo } from "react";
import { Trash, MagnifyingGlass, CaretRight, Lightning, Copy } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ScrollArea } from "../components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";
import { listarRequisicoes, limparRequisicoes } from "../lib/api";
import DialogConfirmacao from "./DialogConfirmacao";

const corMetodo = (m) => {
    switch ((m || "").toUpperCase()) {
        case "GET": return "text-emerald-400";
        case "POST": return "text-blue-400";
        case "PUT": return "text-yellow-400";
        case "DELETE": return "text-red-400";
        case "PATCH": return "text-purple-400";
        default: return "text-zinc-400";
    }
};

const corStatus = (s) => {
    if (!s) return "text-zinc-500";
    if (s >= 500) return "text-red-400";
    if (s >= 400) return "text-yellow-400";
    if (s >= 300) return "text-cyan-400";
    return "text-zinc-300";
};

// Filtros por tipo de recurso, estilo DevTools.
// Mapeamento dos `tipo_recurso` que o chromedp devolve para os agrupamentos amigáveis.
const FILTROS = [
    { id: "todos",  rotulo: "Todos",     tipos: null },
    { id: "fetch",  rotulo: "Fetch/XHR", tipos: ["XHR", "Fetch"] },
    { id: "doc",    rotulo: "Doc",       tipos: ["Document"] },
    { id: "js",     rotulo: "JS",        tipos: ["Script"] },
    { id: "css",    rotulo: "CSS",       tipos: ["Stylesheet"] },
    { id: "img",    rotulo: "Imagens",   tipos: ["Image"] },
    { id: "media",  rotulo: "Mídia",     tipos: ["Media"] },
    { id: "font",   rotulo: "Fonts",     tipos: ["Font"] },
    { id: "ws",     rotulo: "WS",        tipos: ["WebSocket", "EventSource"] },
    { id: "outros", rotulo: "Outros",    tipos: ["Other", "Manifest", "Ping", "CSPViolationReport", "Preflight", "SignedExchange", ""] },
];

export default function AbaRequisicoes({ botId, requisicoesAoVivo, onCriarRegraDeRequisicao, onLimparAoVivo }) {
    const [historico, setHistorico] = useState([]);
    const [filtroTexto, setFiltroTexto] = useState("");
    const [filtroTipo, setFiltroTipo] = useState("fetch"); // default: Fetch/XHR como DevTools
    const [selecionada, setSelecionada] = useState(null);

    const recarregar = async () => {
        try {
            const lista = await listarRequisicoes(botId, 500);
            setHistorico(lista);
        } catch {
            toast.error("Falha ao carregar requisições");
        }
    };

    useEffect(() => {
        recarregar();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [botId]);

    // Mescla requisicoes ao vivo com histórico (id único). Mantém ordem por capturado_em desc.
    const todas = useMemo(() => {
        const mapa = new Map();
        for (const r of historico) mapa.set(r.id, r);
        for (const r of requisicoesAoVivo) mapa.set(r.id, r);
        const arr = Array.from(mapa.values());
        arr.sort((a, b) => new Date(b.capturado_em) - new Date(a.capturado_em));
        return arr;
    }, [requisicoesAoVivo, historico]);

    // Conta total por filtro (mostrar no badge da pill)
    const contagens = useMemo(() => {
        const c = { todos: todas.length };
        for (const f of FILTROS) {
            if (f.id === "todos") continue;
            c[f.id] = todas.filter((r) => f.tipos.includes(r.tipo_recurso || "")).length;
        }
        return c;
    }, [todas]);

    const filtradas = useMemo(() => {
        const q = filtroTexto.trim().toLowerCase();
        const filtro = FILTROS.find((f) => f.id === filtroTipo);
        return todas.filter((r) => {
            if (filtro && filtro.tipos && !filtro.tipos.includes(r.tipo_recurso || "")) return false;
            if (q) {
                return (
                    r.url?.toLowerCase().includes(q) ||
                    r.metodo?.toLowerCase().includes(q) ||
                    String(r.status_codigo).includes(q)
                );
            }
            return true;
        });
    }, [todas, filtroTexto, filtroTipo]);

    const [confirmarLimpar, setConfirmarLimpar] = useState(false);
    const executarLimpar = async () => {
        try {
            await limparRequisicoes(botId);
            setHistorico([]); // limpa lista local
            if (onLimparAoVivo) onLimparAoVivo(); // zera o buffer "ao vivo" no pai
            setSelecionada(null);
            toast.success("Histórico limpo");
        } catch {
            toast.error("Falha");
        }
    };

    return (
        <div className="space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
                <div className="relative flex-1 min-w-[200px]">
                    <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                    <Input
                        data-testid="input-filtro-requisicoes"
                        placeholder="Filtrar por URL, método ou status..."
                        value={filtroTexto}
                        onChange={(e) => setFiltroTexto(e.target.value)}
                        className="bg-zinc-900 border-zinc-800 focus:border-zinc-600 pl-9 text-xs font-mono"
                    />
                </div>
                <Button size="sm" variant="outline" data-testid="btn-recarregar-requisicoes"
                    className="bg-transparent border-zinc-800 hover:bg-zinc-800" onClick={recarregar}>
                    Recarregar
                </Button>
                <Button size="sm" variant="outline" data-testid="btn-limpar-requisicoes"
                    className="bg-red-500/10 border-red-500/30 hover:bg-red-500/20 text-red-400"
                    onClick={() => setConfirmarLimpar(true)}>
                    <Trash size={14} />
                </Button>
            </div>

            {/* Pills de filtro de tipo (igual DevTools) */}
            <div className="flex items-center gap-1 overflow-x-auto hide-scrollbar pb-1" data-testid="filtros-tipo">
                {FILTROS.map((f) => {
                    const ativo = filtroTipo === f.id;
                    const total = contagens[f.id] || 0;
                    return (
                        <button
                            key={f.id}
                            onClick={() => setFiltroTipo(f.id)}
                            data-testid={`pill-filtro-${f.id}`}
                            className={`px-2.5 py-1 rounded-full text-[11px] font-mono uppercase tracking-wider transition-colors whitespace-nowrap flex items-center gap-1.5 ${
                                ativo
                                    ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/40"
                                    : "bg-zinc-900 text-zinc-400 border border-zinc-800 hover:bg-zinc-800 hover:text-zinc-200"
                            }`}
                        >
                            {f.rotulo}
                            {total > 0 && (
                                <span className={`text-[9px] tabular-nums ${ativo ? "text-emerald-400/80" : "text-zinc-600"}`}>
                                    {total}
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>

            <div className="bg-zinc-900 border border-zinc-800 rounded-md">
                <div className="grid grid-cols-[60px_50px_70px_1fr_70px] text-[10px] uppercase tracking-wider text-zinc-500 font-mono px-3 py-2 border-b border-zinc-800">
                    <div>Método</div>
                    <div>Status</div>
                    <div>Tipo</div>
                    <div>URL</div>
                    <div className="text-right">Tempo</div>
                </div>
                <ScrollArea className="h-[400px] sm:h-[500px]">
                    {filtradas.length === 0 ? (
                        <div className="text-center text-zinc-600 py-12 text-sm">
                            {todas.length === 0
                                ? "Nenhuma requisição capturada ainda."
                                : `Nenhuma requisição corresponde ao filtro "${FILTROS.find(f => f.id === filtroTipo)?.rotulo}".`}
                        </div>
                    ) : (
                        <div data-testid="lista-requisicoes">
                            {filtradas.map((req) => (
                                <button
                                    key={req.id}
                                    data-testid={`req-${req.id}`}
                                    onClick={() => setSelecionada(req)}
                                    className="grid grid-cols-[60px_50px_70px_1fr_70px] items-center text-xs font-mono w-full text-left px-3 py-1.5 border-b border-zinc-800/50 hover:bg-zinc-800/50 transition-colors anim-deslizar"
                                >
                                    <span className={`${corMetodo(req.metodo)} font-bold`}>{req.metodo}</span>
                                    <span className={corStatus(req.status_codigo)}>{req.status_codigo || "—"}</span>
                                    <span className="text-zinc-500 text-[10px] truncate">{req.tipo_recurso || "—"}</span>
                                    <span className="truncate text-zinc-300">{req.url}</span>
                                    <span className="text-right text-zinc-500">{req.duracao_ms}ms</span>
                                </button>
                            ))}
                        </div>
                    )}
                </ScrollArea>
            </div>

            <Dialog open={!!selecionada} onOpenChange={(o) => !o && setSelecionada(null)}>
                <DialogContent className="bg-zinc-950 border border-zinc-800 text-zinc-100 max-w-3xl">
                    <DialogHeader>
                        <DialogTitle className="font-medium tracking-tight flex items-center gap-2 flex-wrap">
                            <span className={`${corMetodo(selecionada?.metodo)} font-mono`}>{selecionada?.metodo}</span>
                            <span className={`${corStatus(selecionada?.status_codigo)} font-mono`}>{selecionada?.status_codigo}</span>
                            <CaretRight size={12} className="text-zinc-600" />
                            <span className="font-mono text-xs truncate max-w-[400px]">{selecionada?.url}</span>
                        </DialogTitle>
                    </DialogHeader>
                    {selecionada && (
                        <Tabs defaultValue="hreq" className="w-full">
                            <TabsList className="bg-zinc-900/50 border border-zinc-800/60 rounded-md flex w-full justify-start overflow-x-auto hide-scrollbar p-1 h-auto">
                                <TabsTrigger value="hreq" className="text-xs">Headers Req</TabsTrigger>
                                <TabsTrigger value="hres" className="text-xs">Headers Res</TabsTrigger>
                                <TabsTrigger value="cookies" className="text-xs">Cookies</TabsTrigger>
                                <TabsTrigger value="bodyreq" className="text-xs">Body Req</TabsTrigger>
                                <TabsTrigger value="bodyres" className="text-xs">Body Res</TabsTrigger>
                            </TabsList>
                            <TabsContent value="hreq" className="mt-3">
                                <BlocoChavesValores dados={selecionada.headers_request} />
                            </TabsContent>
                            <TabsContent value="hres" className="mt-3">
                                <BlocoChavesValores dados={selecionada.headers_response} />
                            </TabsContent>
                            <TabsContent value="cookies" className="mt-3">
                                <PainelCookies req={selecionada} />
                            </TabsContent>
                            <TabsContent value="bodyreq" className="mt-3">
                                <BlocoCodigo conteudo={selecionada.corpo_request} />
                            </TabsContent>
                            <TabsContent value="bodyres" className="mt-3">
                                <BlocoCodigo conteudo={selecionada.corpo_response} />
                            </TabsContent>
                        </Tabs>
                    )}
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono mt-3 flex items-center justify-between gap-2 flex-wrap">
                        <span className="truncate">ID: {selecionada?.id} · Capturado em {selecionada && new Date(selecionada.capturado_em).toLocaleString("pt-BR")}</span>
                        <div className="flex items-center gap-1">
                            <Button size="sm" variant="outline" data-testid="btn-copiar-curl"
                                className="bg-transparent border-zinc-800 hover:bg-zinc-800 text-zinc-300 h-7 text-[10px] px-2"
                                onClick={() => {
                                    if (!selecionada) return;
                                    const cmd = construirCurl(selecionada);
                                    navigator.clipboard.writeText(cmd);
                                    toast.success("cURL copiado");
                                }}>
                                <Copy size={11} className="mr-1" /> cURL
                            </Button>
                            <Button size="sm" data-testid="btn-criar-regra-desta-req"
                                className="bg-emerald-500/10 border border-emerald-500/40 hover:bg-emerald-500/20 text-emerald-300 h-7 text-[10px] px-2"
                                onClick={() => {
                                    if (!selecionada) return;
                                    if (onCriarRegraDeRequisicao) {
                                        onCriarRegraDeRequisicao(selecionada);
                                        setSelecionada(null);
                                        toast.success("Editor de regra aberto na aba Regras");
                                    }
                                }}>
                                <Lightning size={11} weight="fill" className="mr-1" /> Criar regra desta
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            <DialogConfirmacao
                aberto={confirmarLimpar}
                setAberto={setConfirmarLimpar}
                titulo="Limpar histórico de requisições?"
                descricao="Todas as requisições capturadas deste bot serão apagadas permanentemente. As regras e cookies não serão afetados."
                rotuloAcao="Limpar histórico"
                onConfirmar={executarLimpar}
                testId="dialog-limpar-requisicoes"
            />
        </div>
    );
}

function BlocoChavesValores({ dados }) {
    const entradas = Object.entries(dados || {});
    if (entradas.length === 0) {
        return <div className="text-xs text-zinc-600 py-4 text-center">Sem dados</div>;
    }
    return (
        <ScrollArea className="h-[280px] border border-zinc-800 rounded-md bg-zinc-950">
            <div className="font-mono text-xs">
                {entradas.map(([k, v]) => (
                    <div key={k} className="grid grid-cols-[180px_1fr] gap-3 px-3 py-1.5 border-b border-zinc-800/50">
                        <span className="text-zinc-500 truncate" title={k}>{k}</span>
                        <span className="text-zinc-200 break-all">{String(v)}</span>
                    </div>
                ))}
            </div>
        </ScrollArea>
    );
}

function BlocoCodigo({ conteudo }) {
    if (!conteudo) return <div className="text-xs text-zinc-600 py-4 text-center">Sem corpo</div>;
    let formatado = conteudo;
    try { formatado = JSON.stringify(JSON.parse(conteudo), null, 2); } catch {}
    return (
        <ScrollArea className="h-[280px] border border-zinc-800 rounded-md bg-zinc-950">
            <pre className="text-xs font-mono p-3 text-zinc-300 whitespace-pre-wrap break-all">{formatado}</pre>
        </ScrollArea>
    );
}

// PainelCookies: extrai e mostra cookies enviados (header "Cookie" da request)
// e recebidos (cabeçalhos "Set-Cookie" da response) em formato tabular.
function PainelCookies({ req }) {
    const enviados = parseCookieHeader(pegarHeaderCI(req.headers_request, "Cookie"));
    const recebidosRaw = pegarHeaderCI(req.headers_response, "Set-Cookie") || "";
    const recebidos = parseSetCookieHeader(recebidosRaw);

    if (enviados.length === 0 && recebidos.length === 0) {
        return <div className="text-xs text-zinc-600 py-4 text-center">Esta requisição não enviou nem recebeu cookies.</div>;
    }

    return (
        <ScrollArea className="h-[280px] border border-zinc-800 rounded-md bg-zinc-950">
            <div className="font-mono text-xs">
                {enviados.length > 0 && (
                    <>
                        <div className="px-3 py-2 bg-zinc-900/80 text-[10px] uppercase tracking-wider text-zinc-500 sticky top-0">
                            Enviados ({enviados.length}) — header Cookie
                        </div>
                        <div className="grid grid-cols-[180px_1fr] gap-3 px-3 py-1.5 border-b border-zinc-800 text-[10px] uppercase tracking-wider text-zinc-600">
                            <span>Nome</span><span>Valor</span>
                        </div>
                        {enviados.map((c, i) => (
                            <div key={`s-${i}`} className="grid grid-cols-[180px_1fr] gap-3 px-3 py-1.5 border-b border-zinc-800/50">
                                <span className="text-emerald-400 truncate" title={c.nome}>{c.nome}</span>
                                <span className="text-zinc-200 break-all">{c.valor}</span>
                            </div>
                        ))}
                    </>
                )}
                {recebidos.length > 0 && (
                    <>
                        <div className="px-3 py-2 bg-zinc-900/80 text-[10px] uppercase tracking-wider text-zinc-500 sticky top-0">
                            Recebidos ({recebidos.length}) — Set-Cookie
                        </div>
                        <div className="grid grid-cols-[150px_1fr_auto] gap-3 px-3 py-1.5 border-b border-zinc-800 text-[10px] uppercase tracking-wider text-zinc-600">
                            <span>Nome</span><span>Valor</span><span>Atributos</span>
                        </div>
                        {recebidos.map((c, i) => (
                            <div key={`r-${i}`} className="grid grid-cols-[150px_1fr_auto] gap-3 px-3 py-1.5 border-b border-zinc-800/50 items-start">
                                <span className="text-cyan-400 truncate" title={c.nome}>{c.nome}</span>
                                <span className="text-zinc-200 break-all">{c.valor}</span>
                                <span className="text-[10px] text-zinc-500 break-all max-w-[180px]">{c.atributos}</span>
                            </div>
                        ))}
                    </>
                )}
            </div>
        </ScrollArea>
    );
}

// pegarHeaderCI: lookup case-insensitive (HTTP headers são case-insensitive).
function pegarHeaderCI(headers, nome) {
    if (!headers) return "";
    if (headers[nome] != null) return headers[nome];
    const alvo = nome.toLowerCase();
    for (const k of Object.keys(headers)) {
        if (k.toLowerCase() === alvo) return headers[k];
    }
    return "";
}

// parseCookieHeader: "a=1; b=2" → [{nome:"a", valor:"1"}, ...]
function parseCookieHeader(str) {
    if (!str) return [];
    return String(str).split(";").map((s) => {
        const eq = s.indexOf("=");
        if (eq === -1) return { nome: s.trim(), valor: "" };
        return { nome: s.slice(0, eq).trim(), valor: s.slice(eq + 1).trim() };
    }).filter((c) => c.nome);
}

// parseSetCookieHeader: pode ter múltiplos Set-Cookie concatenados (rara mas possível).
// Cada um: "NOME=VALOR; Path=/; HttpOnly; Secure; SameSite=Lax"
function parseSetCookieHeader(str) {
    if (!str) return [];
    // Múltiplos Set-Cookie podem vir separados por \n ou (incorretamente) por vírgula+espaço.
    // Heurística: divide por \n, depois por ", " somente se seguido de KEY=
    const linhas = String(str).split(/\n/).flatMap((l) => l.split(/,\s+(?=[A-Za-z0-9_-]+=)/));
    return linhas.map((l) => {
        const partes = l.split(";");
        const principal = partes[0] || "";
        const eq = principal.indexOf("=");
        if (eq === -1) return null;
        return {
            nome: principal.slice(0, eq).trim(),
            valor: principal.slice(eq + 1).trim(),
            atributos: partes.slice(1).map((p) => p.trim()).filter(Boolean).join(" · "),
        };
    }).filter(Boolean);
}

// Monta um comando cURL a partir de uma requisição capturada.
function construirCurl(req) {
    if (!req) return "";
    const partes = [`curl -X ${req.metodo}`];
    partes.push(`'${req.url}'`);
    const headers = req.headers_request || {};
    for (const [k, v] of Object.entries(headers)) {
        partes.push(`-H '${k}: ${String(v).replace(/'/g, "'\\''")}'`);
    }
    if (req.corpo_request) {
        partes.push(`--data-raw '${String(req.corpo_request).replace(/'/g, "'\\''")}'`);
    }
    return partes.join(" \\\n  ");
}
